import { Component } from '@theme/component';

/**
 * BB member signup modal opened from the header account action.
 *
 * @typedef {object} Refs
 * @property {HTMLDivElement} overlay - Modal overlay.
 * @property {HTMLButtonElement} closeButton - Close button.
 * @property {HTMLDivElement} backdrop - Modal backdrop.
 *
 * @extends Component<Refs>
 */
class NeoSignupModal extends Component {
  #previousScrollY = 0;
  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  connectedCallback() {
    super.connectedCallback();

    requestIdleCallback(() => {
      this.#bindEvents();
    });
  }

  disconnectedCallback() {
    this.#unbindEvents();
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleKeyDown);
  }

  #bindEvents() {
    this.refs.closeButton?.addEventListener('click', this.closeModal);
    this.refs.backdrop?.addEventListener('click', this.closeModalOnBackdropClick);
    document.addEventListener('click', this.#handleTriggerClick);
  }

  #unbindEvents() {
    this.refs.closeButton?.removeEventListener('click', this.closeModal);
    this.refs.backdrop?.removeEventListener('click', this.closeModalOnBackdropClick);
    document.removeEventListener('click', this.#handleTriggerClick);
  }

  /**
   * Opens the signup modal from a header trigger.
   *
   * @param {HTMLElement} [trigger] - Element that opened the modal.
   */
  openModal = (trigger) => {
    const { overlay } = this.refs;

    if (!overlay?.hasAttribute('hidden')) return;

    this.#lastTrigger = trigger ?? null;
    this.#previousScrollY = window.scrollY;
    overlay.removeAttribute('hidden');
    document.documentElement.setAttribute('scroll-lock', '');
    document.addEventListener('keydown', this.#handleKeyDown);
    this.refs.closeButton?.focus();
  };

  /**
   * Closes the signup modal.
   */
  closeModal = () => {
    const { overlay } = this.refs;

    if (!overlay || overlay.hasAttribute('hidden')) return;

    overlay.setAttribute('hidden', '');
    document.documentElement.removeAttribute('scroll-lock');
    document.removeEventListener('keydown', this.#handleKeyDown);
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#lastTrigger?.focus();
    this.#lastTrigger = null;
  };

  /**
   * Opens the modal when a header trigger is clicked.
   *
   * @param {MouseEvent} event - Click event.
   */
  #handleTriggerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-neo-signup-trigger]');

    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    this.openModal(trigger);
  };

  /**
   * Closes the modal when the backdrop is clicked.
   *
   * @param {MouseEvent} event - Click event.
   */
  closeModalOnBackdropClick = (event) => {
    if (event.target !== this.refs.backdrop) return;
    this.closeModal();
  };

  /**
   * Closes the modal on Escape key press.
   *
   * @param {KeyboardEvent} event - Keyboard event.
   */
  #handleKeyDown = (event) => {
    const { overlay } = this.refs;

    if (event.key !== 'Escape' || !overlay || overlay.hasAttribute('hidden')) return;

    event.preventDefault();
    this.closeModal();
  };
}

if (!customElements.get('neo-signup-modal')) {
  customElements.define('neo-signup-modal', NeoSignupModal);
}
